import type { MetaFunction } from "@remix-run/cloudflare";
import { Link } from "@remix-run/react";
import { useState } from "react";
import { 
  HelpCircle, 
  ChevronDown, 
  ChevronUp,
  GamepadIcon,
  Package,
  Globe,
  Monitor,
  Search
} from "lucide-react";

export const meta: MetaFunction = () => {
  return [
    { title: "FAQ - Stellar Blade Mods Wiki" },
    { 
      name: "description", 
      content: "Frequently asked questions about Stellar Blade mods. Find quick answers to common modding questions, installation issues, and technical support." 
    },
    { name: "keywords", content: "stellar blade,faq,frequently asked questions,mod help,installation problems,technical support" },
    { name: "robots", content: "index,follow" },
    { rel: "canonical", href: "https://stellarblademod.com/wiki/faq" },
    
    // Open Graph
    { property: "og:title", content: "FAQ - Stellar Blade Mods Wiki" },
    { property: "og:description", content: "Frequently asked questions about Stellar Blade mods. Find quick answers to common modding questions, installation issues, and technical support." },
    { property: "og:url", content: "https://stellarblademod.com/wiki/faq" },
    { property: "og:type", content: "website" },
    
    // Twitter Card
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "FAQ - Stellar Blade Mods Wiki" },
    { name: "twitter:description", content: "Frequently asked questions about Stellar Blade mods. Find quick answers to common modding questions, installation issues, and technical support." },
  ];
};

const faqCategories = [
  {
    id: "game-issues",
    title: "Game Issues",
    icon: GamepadIcon,
    color: "from-red-400 to-pink-500",
    questions: [
      {
        question: "Why does my game crash after installing mods?",
        answer: "Game crashes after mod installation are usually caused by mod conflicts, corrupted files, or incompatible mods. Try installing one mod at a time to identify the problematic mod. Also ensure you're using the correct version of the mod for your game version."
      },
      {
        question: "My game won't start after installing mods. What should I do?",
        answer: "If the game won't start, first try verifying your game files through Steam. If that doesn't work, remove all mods and try starting the game. If it starts normally, reinstall mods one by one to identify the issue."
      },
      {
        question: "Are mods safe to use with Stellar Blade?",
        answer: "Most mods from reputable sources are safe to use. However, always backup your save files before installing mods. For additional security, scan mod files using VirusTotal (https://www.virustotal.com/gui/home/upload) before installation. Only download mods from trusted sources and read user reviews before installation."
      },
      {
        question: "Will using mods affect my save files?",
        answer: "Some mods may modify save files, while others don't. Always create a backup of your save files before installing any mods. Check the mod description to see if it affects save files."
      },
      {
        question: "How can I check if a mod file is safe before installing?",
        answer: "Use VirusTotal (https://www.virustotal.com/gui/home/upload) to scan mod files before installation. Simply upload the mod file to VirusTotal and it will scan it with 70+ antivirus engines to detect any potential threats. This free service provides detailed security reports and is especially useful when downloading from unfamiliar sources."
      }
    ]
  },
  {
    id: "mod-installation",
    title: "Mod Installation",
    icon: Package,
    color: "from-green-400 to-emerald-500",
    questions: [
      {
        question: "How do I install mods for Stellar Blade?",
        answer: "Installation methods vary by mod type. Most mods come with installation instructions. Generally, you'll need to extract files to specific game directories. Always read the mod's README file for specific instructions."
      },
      {
        question: "Can I install multiple mods at the same time?",
        answer: "Yes, but be careful about mod conflicts. Some mods may modify the same game files and cause issues. Check mod compatibility and load order. Install one mod at a time and test the game between installations."
      },
      {
        question: "How do I uninstall mods?",
        answer: "To uninstall mods, remove the mod files you added to the game directory. If you're unsure which files belong to the mod, use Steam's 'Verify Integrity of Game Files' feature to restore original files."
      },
      {
        question: "What's the difference between manual and automatic installation?",
        answer: "Manual installation requires you to manually copy files to game directories. Automatic installation uses mod managers that handle file placement automatically. Automatic installation is generally easier and safer for beginners."
      }
    ]
  },
  {
    id: "website-usage",
    title: "Website Usage",
    icon: Globe,
    color: "from-blue-400 to-cyan-500",
    questions: [
      {
        question: "Do I need an account to download mods?",
        answer: "Currently, most mods can be downloaded without an account. However, creating an account allows you to rate mods, leave comments, and receive updates about your favorite mods."
      },
      {
        question: "How do I report a broken or malicious mod?",
        answer: "If you encounter a broken or suspicious mod, please contact us through the contact page with details about the mod and the issue. We take all reports seriously and will investigate promptly."
      },
      {
        question: "Can I upload my own mods to this site?",
        answer: "We're planning to add mod upload functionality in the future. Currently, we're focusing on curating high-quality mods from the community. Stay tuned for updates on mod submissions."
      },
      {
        question: "How often are new mods added?",
        answer: "We regularly review and add new mods to our collection. The frequency depends on community contributions and mod quality. Follow us for updates on new additions."
      }
    ]
  },
  {
    id: "technical-support",
    title: "Technical Support",
    icon: Monitor,
    color: "from-purple-400 to-indigo-500",
    questions: [
      {
        question: "What are the system requirements for modded Stellar Blade?",
        answer: "Modded games may require additional system resources. Generally, you'll need the base game's requirements plus extra RAM and storage space. Some graphics mods may require more powerful GPUs."
      },
      {
        question: "My mods work on PC but not on console. Why?",
        answer: "Stellar Blade modding is primarily available for PC versions. Console modding has significant limitations due to system restrictions and security measures. Most mods are designed specifically for PC."
      },
      {
        question: "How do I check if my game version is compatible with a mod?",
        answer: "Check the mod description for version compatibility information. Most mods specify which game versions they support. Using incompatible versions may cause crashes or other issues."
      },
      {
        question: "Where can I get additional technical help?",
        answer: "For technical issues not covered in this FAQ, check our Knowledge Base for detailed guides. You can also contact our support team through the contact page with specific details about your issue."
      }
    ]
  }
];

export default function FAQ() {
  const [openQuestions, setOpenQuestions] = useState<Record<string, boolean>>({});
  const [searchTerm, setSearchTerm] = useState("");

  const toggleQuestion = (categoryId: string, questionIndex: number) => {
    const key = `${categoryId}-${questionIndex}`;
    setOpenQuestions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const filteredCategories = faqCategories.map(category => ({
    ...category,
    questions: category.questions.filter(
      q => 
        q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.answer.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
      {/* Header */}
      <div className="relative pt-20 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <HelpCircle className="w-12 h-12 text-purple-400 mr-4" />
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                FAQ <span className="text-amber-400 text-2xl md:text-3xl">(under construction)</span>
              </h1>
            </div>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Quick answers to the most frequently asked questions about Stellar Blade modding
            </p>
            
            {/* Breadcrumb */}
            <nav className="flex items-center justify-center space-x-2 text-sm mb-8">
              <Link to="/wiki" className="text-cyan-400 hover:text-cyan-300">
                Wiki
              </Link>
              <span className="text-gray-500">/</span>
              <span className="text-gray-300">FAQ</span>
            </nav>

            {/* Search */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search FAQ..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        {filteredCategories.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">No questions found matching your search.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {filteredCategories.map((category) => (
              <div key={category.id} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8">
                {/* Category Header */}
                <div className="flex items-center mb-8">
                  <div className={`p-3 bg-gradient-to-r ${category.color} rounded-lg mr-4`}>
                    <category.icon className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold text-white">{category.title}</h2>
                </div>

                {/* Questions */}
                <div className="space-y-4">
                  {category.questions.map((qa, index) => {
                    const isOpen = openQuestions[`${category.id}-${index}`];
                    return (
                      <div key={index} className="bg-white/5 border border-white/10 rounded-lg">
                        <button
                          onClick={() => toggleQuestion(category.id, index)}
                          className="w-full flex items-center justify-between p-6 text-left hover:bg-white/5 transition-colors rounded-lg"
                        >
                          <h3 className="text-lg font-semibold text-white pr-4">
                            {qa.question}
                          </h3>
                          {isOpen ? (
                            <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                          )}
                        </button>
                        
                        {isOpen && (
                          <div className="px-6 pb-6">
                            <div className="border-t border-white/10 pt-4">
                              <p className="text-gray-300 leading-relaxed">
                                {qa.answer}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8">
            <h3 className="text-2xl font-bold text-white mb-4">Still Need Help?</h3>
            <p className="text-gray-300 mb-6">
              Can't find the answer you're looking for? Check our detailed Knowledge Base or contact our support team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/wiki/knowledge-base"
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-cyan-400 to-blue-500 text-white font-semibold rounded-lg hover:from-cyan-500 hover:to-blue-600 transition-all duration-300"
              >
                Browse Knowledge Base
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center px-6 py-3 bg-white/10 border border-white/20 text-white font-semibold rounded-lg hover:bg-white/20 transition-all duration-300"
              >
                Contact Support
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 